import { createNamespacedHelpers } from 'vuex';
import {
  findEntity,
  findEntityOrFail,
  mapEntity,
  mapFindByKeyGetters,
  mapStoreGetters,
  mapStoreMutations,
  mapStoreState,
  prefixTypeFactory,
} from '../../../utils/StoreHelper';
import { getData } from '../../../api/api';
import { sortProducts } from '../../../utils/ProductUtils';
import {
  productVariantIndex,
  readFromIndex,
  sortAttributeOption,
} from '../../../utils/GlassesUtils';
import { ROOT_CATEGORY_ID } from '../../../api/static/categories';
import { PrintTypeId } from '../../../api/static/printTypes';
import { GENERIC_COLORS } from '../../../api/static/colors';
import {
  ConfiguratorMutation,
  prefixType as prefixConfiguratorType,
} from '../configurator';
import { ThemeType } from '../../../api/static/ThemeType';
import { DEFAULT_ZONE_GROUP_ID } from '../../../api/static/zoneGroups';
import { DEFAULT_SIZE_ID, SizeId } from '../../../api/static/sizes';
import { GraduationImage } from '../../../api/static/graduations';
import { MiscMutation, prefixType as prefixMiscType } from './misc';

export const NAMESPACE = 'configurator/glasses';

export const { mapState, mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers(NAMESPACE);

/**
 * @type {function(string): string}
 */
export const prefixType = prefixTypeFactory(NAMESPACE);

/**
 * @enum
 * @readonly
 */
export const GlassesMutation = Object.freeze({
  SET_CATEGORIES: 'setCategories',
  SET_PRODUCTS: 'setProducts',
  SET_SIZES: 'setSizes',
  SET_QUANTITIES: 'setQuantities',
  SET_PRINT_TYPES: 'setPrintTypes',
  SET_ZONES: 'setZones',
  SET_ZONE_GROUPS: 'setZoneGroups',
  SET_COLORS: 'setColors',
  SET_SCREEN_PRINTING_COLORS: 'setScreenPrintingColors',
  SET_TYPOS: 'setTypos',
  SET_PRODUCT_VARIANTS: 'setProductVariants',
  SET_CONFIGURATIONS: 'setConfigurations',
  SET_PRINTING_AREAS: 'setPrintingAreas',
});

/**
 * @enum
 * @readonly
 */
export const GlassesAction = Object.freeze({
  INIT_STATE: 'initState',
});

/**
 * @enum
 * @readonly
 */
export const GlassesGetter = Object.freeze({
  GET_PRODUCT_VARIANTS: 'getProductVariants',
  GET_PRODUCTS_BY_CATEGORY: 'getProductsByCategory',
  GET_COUNT_BY_CATEGORY: 'getCountByCategory',
  FIND_CATEGORY_BY_PARENT_ID: 'findCategoryByParentId',
  FIND_CATEGORY_BY_ID: 'findCategoryById',
  GET_MATCHING_GENERIC_THEMES: 'getMatchingGenericThemes',
  FILTER_THEMES: 'filterThemes',
  GET_CUSTOM_THEMES: 'getCustomThemes',
  GET_CUSTOM_CMYK_THEMES: 'getCustomCmykThemes',
  GET_CUSTOM_SCREEN_PRINTING_THEMES: 'getCustomScreenPrintingThemes',
  GET_PRODUCT_VARIANT_INDEX: 'getProductVariantIndex',
  FIND_PRODUCT_VARIANTS: 'findProductVariants',
  GET_PRODUCT_VARIANT_QUANTITIES: 'getProductVariantQuantities',
  GET_PRODUCT_VARIANT_SIZES: 'getProductVariantSizes',
  GET_SIZES: 'getSizes',
  GET_TYPOS: 'getTypos',
  GET_SCREEN_PRINTING_COLORS: 'getScreenPrintingColors',
  FILTER_PRODUCT_VARIANTS: 'filterProductVariants',
  GET_MATCHING_QUANTITIES: 'getMatchingQuantities',
  GET_SIZE_BY_PRODUCT_ID: 'getSizeByProductId',
  FIND_PRODUCT_BY_ID: 'findProductById',
  FIND_SIZE_BY_ID: 'findSizeById',
  FIND_QUANTITY_BY_ID: 'findQuantityById',
  FIND_COLOR_BY_ID: 'findColorById',
  FIND_TYPO_BY_ID: 'findTypoById',
  FIND_PRODUCT_VARIANT: 'findProductVariant',
  FIND_CONFIGURATION_BY_ID: 'findConfigurationById',
  FIND_PRINTING_AREA_BY_ID: 'findPrintingAreaById',
  FIND_ZONE_BY_ID: 'findZoneById',
  MAP_ZONES: 'mapZones',
  FILTER_MATCHING_PRODUCTS: 'filterMatchingProducts',
  FIND_SCREEN_PRINTING_COLOR_BY_ID: 'findScreenPrintingColorById',
  FIND_AVAILABLE_COLORS_BY_PRINT_TYPE_ID: 'findAvailableColors',
  FIND_GRADUATION_IMAGE: 'findGraduationImage',
  FIND_GRADUATION_WIDTH: 'findGraduationWidth',
  /**
   * @deprecated
   */
  GROUP_BY_ZONE_GROUP: 'groupByZoneGroup',
  GET_GROUP_LIST_FROM_ZONES: 'getGroupListFromZones',
});

export default {
  namespaced: true,
  state() {
    return {
      ...mapStoreState([
        'color',
        'screenPrintingColors',
        'category',
        'product',
        'size',
        'quantity',
        'printType',
        'zone',
        'zoneGroup',
        'typo',
        'productVariant',
        'configuration',
        'printingArea',
      ]),
    };
  },
  mutations: {
    ...mapStoreMutations(
      {
        [GlassesMutation.SET_COLORS]: { prop: 'color' },
        [GlassesMutation.SET_SCREEN_PRINTING_COLORS]: {
          prop: 'screenPrintingColors',
        },
        [GlassesMutation.SET_CATEGORIES]: { prop: 'category' },
        [GlassesMutation.SET_PRODUCTS]: { prop: 'product' },
        [GlassesMutation.SET_SIZES]: { prop: 'size' },
        [GlassesMutation.SET_QUANTITIES]: { prop: 'quantity' },
        [GlassesMutation.SET_PRINT_TYPES]: { prop: 'printType' },
        [GlassesMutation.SET_ZONES]: { prop: 'zone' },
        [GlassesMutation.SET_ZONE_GROUPS]: { prop: 'zoneGroup' },
        [GlassesMutation.SET_TYPOS]: { prop: 'typo' },
        [GlassesMutation.SET_PRODUCT_VARIANTS]: { prop: 'productVariant' },
        [GlassesMutation.SET_CONFIGURATIONS]: { prop: 'configuration' },
        [GlassesMutation.SET_PRINTING_AREAS]: { prop: 'printingArea' },
      },
      true
    ),
  },
  actions: {
    async [GlassesAction.INIT_STATE]({ commit }, displayDrafts = false) {
      const {
        categories,
        colors,
        products,
        sizes,
        quantities,
        printTypes,
        zones,
        configurationFieldGroups: zoneGroups,
        typos,
        productVariants,
        configurations,
        printingAreas,
        screenPrintingColors,
        ctas,
        misc: { fileUploadDescription },
      } = await getData(displayDrafts);

      commit(GlassesMutation.SET_COLORS, colors);
      commit(GlassesMutation.SET_SCREEN_PRINTING_COLORS, screenPrintingColors);
      commit(GlassesMutation.SET_CATEGORIES, categories);
      commit(GlassesMutation.SET_PRODUCTS, products);
      commit(GlassesMutation.SET_SIZES, sizes);
      commit(GlassesMutation.SET_QUANTITIES, quantities);
      commit(GlassesMutation.SET_PRINT_TYPES, printTypes);
      commit(GlassesMutation.SET_ZONES, zones);
      commit(GlassesMutation.SET_ZONE_GROUPS, zoneGroups);
      commit(GlassesMutation.SET_TYPOS, typos);
      commit(GlassesMutation.SET_PRODUCT_VARIANTS, productVariants);
      commit(GlassesMutation.SET_CONFIGURATIONS, configurations);
      commit(GlassesMutation.SET_PRINTING_AREAS, printingAreas);
      commit(prefixConfiguratorType(ConfiguratorMutation.SET_CTAS), ctas, {
        root: true,
      });
      commit(
        prefixMiscType(MiscMutation.SET_STATE),
        { fileUploadDescription },
        {
          root: true,
        }
      );
    },
  },
  getters: {
    ...mapStoreGetters({
      [GlassesGetter.GET_PRODUCT_VARIANTS]: 'productVariant',
      [GlassesGetter.GET_SIZES]: 'size',
      [GlassesGetter.GET_TYPOS]: 'typo',
    }),
    ...mapFindByKeyGetters({
      [GlassesGetter.FIND_PRODUCT_BY_ID]: 'productMap',
      [GlassesGetter.FIND_SIZE_BY_ID]: 'sizeMap',
      [GlassesGetter.FIND_QUANTITY_BY_ID]: 'quantityMap',
      [GlassesGetter.FIND_COLOR_BY_ID]: 'colorMap',
      [GlassesGetter.FIND_CONFIGURATION_BY_ID]: 'configurationMap',
      [GlassesGetter.FIND_TYPO_BY_ID]: 'typoMap',
      [GlassesGetter.FIND_ZONE_BY_ID]: 'zoneMap',
      [GlassesGetter.FIND_SCREEN_PRINTING_COLOR_BY_ID]:
        'screenPrintingColorsMap',
    }),
    /**
     *
     * @param {ConfiguratorState} state
     * @return {function(*): Color[]}
     */
    [GlassesGetter.FIND_AVAILABLE_COLORS_BY_PRINT_TYPE_ID](state) {
      return () => mapEntity(state.colorMap, GENERIC_COLORS);
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @return {function(*): string}
     */
    [GlassesGetter.FIND_GRADUATION_IMAGE](state, getters, rootState) {
      /** @type {ConfiguratorState} */
      const configuratorState = rootState.configurator;

      const activeSizeId = configuratorState.activeSizeId || DEFAULT_SIZE_ID;
      const glassesWithGraduations = [SizeId.G25, SizeId.G50];

      if (glassesWithGraduations.includes(activeSizeId)) {
        const images = GraduationImage;
        return () => images[activeSizeId];
      } else {
        return () => undefined;
      }
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @return {function(*): number}
     */
    [GlassesGetter.FIND_GRADUATION_WIDTH](state, getters, rootState) {
      /** @type {ConfiguratorState} */
      const configuratorState = rootState.configurator;

      const activeSizeId = configuratorState.activeSizeId || DEFAULT_SIZE_ID;

      switch (activeSizeId) {
        case SizeId.G25:
          return () => 16;
        case SizeId.G50:
          return () => 14;
        default:
          return () => 0;
      }
    },
    /**
     *
     * @param {GlassesState} state
     * @return {Array}
     */
    [GlassesGetter.GET_SCREEN_PRINTING_COLORS](state) {
      return mapEntity(
        state.screenPrintingColorsMap,
        state.screenPrintingColorsList
      );
    },
    /**
     *
     * @param {GlassesState} state
     * @return {Object<CategoryId,ProductId[]>}
     */
    [GlassesGetter.GET_PRODUCTS_BY_CATEGORY](state) {
      // Initialize map
      const categoryMap = Object.values(state.categoryMap).reduce(
        (acc, category) => {
          acc[category.id] = new Set(); // Using Set to avoid duplicates
          return acc;
        },
        {}
      );

      // Populate the map with categories having products
      Object.values(state.productMap).forEach(product => {
        if (product.categories && Array.isArray(product.categories)) {
          product.categories.forEach(categoryId => {
            if (categoryMap[categoryId]) {
              categoryMap[categoryId].add(product.id);
            }
          });
        }
      });

      // Transform Set to Array
      Object.keys(categoryMap).forEach(categoryId => {
        categoryMap[categoryId] = Array.from(categoryMap[categoryId]);
      });

      return categoryMap;
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(CategoryId|null): Category[]}
     */
    [GlassesGetter.FIND_CATEGORY_BY_PARENT_ID](state, getters) {
      return (categoryId = null) => {
        const productsByCategory =
          getters[GlassesGetter.GET_PRODUCTS_BY_CATEGORY];

        return Object.values(state.categoryMap)
          .reduce((acc, category) => {
            if (
              category.parentId === categoryId &&
              category.id !== ROOT_CATEGORY_ID
            ) {
              acc.push({
                ...category,
                count: productsByCategory[category.id].length,
              });
            }

            return acc;
          }, [])
          .sort((a, b) => a.name.localeCompare(b.name));
      };
    },
    /**
     *
     * @param {GlassesState} state
     * @return {function(*): Category | null}
     */
    [GlassesGetter.FIND_CATEGORY_BY_ID](state) {
      return categoryId => findEntity(state.categoryMap, { _key: categoryId });
    },
    /**
     *
     * @param {GlassesState} state
     * @return {Object<ThemeType, Object<SizeId, Object<ProductId, ProductVariantId[]>>>}
     */
    [GlassesGetter.GET_PRODUCT_VARIANT_INDEX](state) {
      return productVariantIndex(
        Object.values(state.productVariantMap),
        state.productMap
      );
    },
    /**
     *
     * @param {GlassesState} state
     * @return {function(*): PrintingArea | null}
     */
    [GlassesGetter.FIND_PRINTING_AREA_BY_ID](state) {
      return printingAreaId =>
        findEntity(state.printingAreaMap, { _key: printingAreaId });
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(*=, *=, *=): {productVariant: ProductVariant, product: Product, configuration: Configuration, printingArea: PrintingArea}[]}
     */
    [GlassesGetter.FILTER_THEMES](state, getters) {
      /** @type {Object<CategoryId,ProductId[]>} */
      const productIdByCategory =
        getters[GlassesGetter.GET_PRODUCTS_BY_CATEGORY];
      const productVariantIndex =
        getters[GlassesGetter.GET_PRODUCT_VARIANT_INDEX];

      return (themeType, sizeId, categoryId = undefined) => {
        return Object.values(
          readFromIndex(productVariantIndex, themeType, sizeId).reduce(
            (acc, pvId) => {
              const productId = state.productVariantMap[pvId].productId;

              if (acc[productId] === undefined) {
                if (
                  (categoryId &&
                    state.productVariantMap[pvId] &&
                    productIdByCategory[categoryId] &&
                    productIdByCategory[categoryId].includes(productId)) ||
                  !categoryId
                ) {
                  const productVariant = state.productVariantMap[pvId];

                  acc[productId] = {
                    productVariant,
                    product: state.productMap[productId],
                    configuration:
                      state.configurationMap[productVariant.configurationId],
                    printingArea:
                      state.printingAreaMap[productVariant.printingAreaId],
                  };
                }
              }

              return acc;
            },
            {}
          )
        ).sort((a, b) => sortProducts(a.product, b.product));
      };
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @param rootState
     * @return {{productVariant: ProductVariant, product: Product, configuration: Configuration, printingArea: PrintingArea}[]}
     */
    [GlassesGetter.GET_MATCHING_GENERIC_THEMES](state, getters, rootState) {
      /** @type {ConfiguratorState} */
      const configuratorState = rootState.configurator;

      return getters[GlassesGetter.FILTER_THEMES](
        ThemeType.GENERIC,
        configuratorState.activeSizeId,
        configuratorState.activeCategoryId
      );
    },
    [GlassesGetter.GET_CUSTOM_THEMES](state, getters, rootState) {
      /** @type {ConfiguratorState} */
      const configuratorState = rootState.configurator;

      return getters[GlassesGetter.FILTER_THEMES](
        ThemeType.CUSTOM,
        configuratorState.activeSizeId
      );
    },
    [GlassesGetter.GET_CUSTOM_CMYK_THEMES](state, getters) {
      return getters[GlassesGetter.GET_CUSTOM_THEMES].filter(
        item => item.productVariant.printTypeId === PrintTypeId.CMYK
      );
    },
    [GlassesGetter.GET_CUSTOM_SCREEN_PRINTING_THEMES](state, getters) {
      return getters[GlassesGetter.GET_CUSTOM_THEMES].filter(
        item => item.productVariant.printTypeId === PrintTypeId.SCREEN_PRINTING
      );
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @param rootState
     * @return {Quantity[]}
     */
    [GlassesGetter.GET_MATCHING_QUANTITIES](state, getters, rootState) {
      /** @type {ConfiguratorState} */
      const configuratorState = rootState.configurator;
      const productVariantIndex =
        getters[GlassesGetter.GET_PRODUCT_VARIANT_INDEX];

      const productVariants = readFromIndex(
        productVariantIndex,
        configuratorState.themeType,
        configuratorState.activeSizeId,
        configuratorState.activeProductId
      );

      return Object.values(
        productVariants.reduce((acc, pvId) => {
          const quantityId = state.productVariantMap[pvId].quantityId;

          if (
            acc[quantityId] === undefined &&
            state.quantityMap[quantityId] !== undefined
          ) {
            acc[quantityId] = state.quantityMap[quantityId];
          }

          return acc;
        }, {})
      ).sort(sortAttributeOption);
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(*=, *=, *=): ProductVariantId[]}
     */
    [GlassesGetter.FILTER_PRODUCT_VARIANTS](state, getters) {
      const index = getters[GlassesGetter.GET_PRODUCT_VARIANT_INDEX];

      return (themeType, sizeId, productId) =>
        readFromIndex(index, themeType, sizeId, productId);
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(*=, *=, *=): Product[]}
     */
    [GlassesGetter.FILTER_MATCHING_PRODUCTS](state, getters) {
      return (themeType, sizeId, productId) =>
        Object.values(
          getters[GlassesGetter.FILTER_PRODUCT_VARIANTS](
            themeType,
            sizeId,
            productId
          ).reduce((acc, pvId) => {
            const pId = state.productVariantMap[pvId].productId;

            if (acc[pId] === undefined) {
              acc[pId] = state.productMap[pId];
            }

            return acc;
          }, {})
        ).sort((a, b) => sortProducts(a, b));
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(*=, *=, *=): ProductVariantId[]|[]}
     */
    [GlassesGetter.FIND_PRODUCT_VARIANTS](state, getters) {
      return (themeType, sizeId, productId) =>
        mapEntity(
          state.productVariantMap,
          getters[GlassesGetter.FILTER_PRODUCT_VARIANTS](
            themeType,
            sizeId,
            productId
          )
        );
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {*}
     */
    [GlassesGetter.FIND_PRODUCT_VARIANT](state, getters) {
      return (themeType, sizeId, productId, quantityId) =>
        getters[GlassesGetter.FIND_PRODUCT_VARIANTS](
          themeType,
          sizeId,
          productId
        ).find(pv => pv.quantityId === quantityId);
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(*=, *=, *=): *}
     */
    [GlassesGetter.GET_PRODUCT_VARIANT_QUANTITIES](state, getters) {
      return (themeType, sizeId, productId) =>
        getters[GlassesGetter.FIND_PRODUCT_VARIANTS](
          themeType,
          sizeId,
          productId
        )
          .map(pv => state.quantityMap[pv.quantityId])
          .sort(sortAttributeOption);
    },
    /**
     *
     * @param {GlassesState} state
     * @param getters
     * @return {function(*=, *=, *=): *}
     */
    [GlassesGetter.GET_PRODUCT_VARIANT_SIZES](state, getters) {
      return (themeType, sizeId, productId) =>
        Object.values(
          getters[GlassesGetter.FIND_PRODUCT_VARIANTS](
            themeType,
            sizeId,
            productId
          ).reduce((acc, pv) => {
            if (!acc[pv.sizeId]) {
              acc[pv.sizeId] = state.sizeMap[pv.sizeId];
            }

            return acc;
          }, {})
        ).sort(sortAttributeOption);
    },
    /**
     *
     * @param {GlassesState} state
     */
    [GlassesGetter.GET_SIZE_BY_PRODUCT_ID](state) {
      // WARNING This getter is not optimized and can lead to performance issues with a large data set
      return productId =>
        Object.values(
          Object.values(state.productVariantMap).reduce((acc, pv) => {
            if (pv.productId === productId && !acc[pv.sizeId]) {
              acc[pv.sizeId] = state.sizeMap[pv.sizeId];
            }

            return acc;
          }, {})
        ).sort(sortAttributeOption);
    },
    /**
     *
     * @param {GlassesState} state
     * @return {function(*): Array}
     */
    [GlassesGetter.MAP_ZONES](state) {
      return zoneIdList => mapEntity(state.zoneMap, zoneIdList);
    },
    /**
     * @deprecated
     * @param state
     * @param getters
     * @returns {*}
     */
    [GlassesGetter.GROUP_BY_ZONE_GROUP](state, getters) {
      return getters[GlassesGetter.GET_GROUP_LIST_FROM_ZONES];
    },
    /**
     *
     * @param {GlassesState} state
     * @returns {function(*): []}
     */
    [GlassesGetter.GET_GROUP_LIST_FROM_ZONES](state) {
      return zoneList =>
        Object.values(
          zoneList.reduce((acc, zone) => {
            if (zone.groupId) {
              if (!acc[zone.groupId]) {
                acc[zone.groupId] = {
                  ...findEntityOrFail(
                    state.zoneGroupMap,
                    zone.groupId,
                    'zoneGroup'
                  ),
                  zoneMap: {},
                };
              }

              acc[zone.groupId].zoneMap[zone.id] = zone;
            } else {
              if (!acc.default) {
                acc.default = {
                  id: DEFAULT_ZONE_GROUP_ID,
                  name: 'default',
                  position: 0,
                  zoneMap: {},
                };
              }

              acc.default.zoneMap[zone.id] = zone;
            }

            return acc;
          }, {})
        ).sort((a, b) => a.position < b.position);
    },
  },
};
